import {parseJWT, getIsDelegationToken, getIsMemberToken} from '@utils/jwtUtils';
import {groupApi} from '@api/group/api';
import {accountUserApi} from '@api/accountUser/api';

/**
 * Resolves the underlying user_id for an actor JWT token.
 *
 * Delegation tokens carry the actor user_id directly in `exchange_actor_user_id`.
 *
 * Member tokens carry a member_id in `exchange_actor_user_id`. To get the user_id
 * we chain:
 *   1. GET /members/{memberId} → account_user_id
 *   2. GET /account-users/{accountUserId} → user_id
 *
 * Returns null when token is not an actor token, the claim is missing, or any
 * step in the chain fails.
 */
export async function resolveActorUserId(accessToken: string | null | undefined): Promise<string | null> {
    if (!accessToken) return null;

    const payload = parseJWT(accessToken);
    const actorClaim = typeof payload?.exchange_actor_user_id === 'string'
        ? payload.exchange_actor_user_id
        : null;

    if (!actorClaim) return null;

    if (getIsDelegationToken(accessToken)) {
        return actorClaim;
    }

    if (getIsMemberToken(accessToken)) {
        try {
            const memberResp = await groupApi.getMember({
                memberId: actorClaim,
                accessToken
            });
            const accountUserId = memberResp?.data?.account_user_id;
            if (!accountUserId) return null;

            const accountUserResp = await accountUserApi.getAccountUser({
                accountUserId,
                options: {accessToken}
            });
            return accountUserResp?.data?.user_id || null;
        } catch {
            return null;
        }
    }

    return null;
}
